import React, { memo, useCallback, useEffect, useState } from 'react';
import { IArgument, IArgumentUpdate, SpecialValue, wereArgsChanged } from '@tymely/atoms';
import { Box, Button, FormHelperText, ListItem, styled } from '@mui/material';
import { ErrorBoundary } from 'react-error-boundary';
import ApprovedIcon from '@tymely/components/Icons/Approved';
import { MEDIA_ARGUMENT_DTYPES } from '@tymely/utils/constants';
import { ArgumentListItemProps } from '@tymely/types/arguments';
import { ArgEditorProps } from '@tymely/features/Ticket/Arguments/Sections/ArgEditor';
import { ArgErrorFallback, Argument } from '@tymely/features';
import { ArgEditor } from '@tymely/features/Ticket/Arguments/Sections';

import { ArgCard } from '../../Arguments/Sections/styled';

const _ListItem = styled(ListItem)`
    padding-left: 0;
    padding-right: 0;
`;

const _ApprovedIconContainer = styled(Box)`
    width: 100%;
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
`;

const _ApproveContainer = styled(Box)`
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
`;

const QaArgumentListItem = memo(
    ({
        argument,
        groupArgument,
        handleApprove,
        displayRegulatorArgument,
        searchArgument,
        withApproval,
        approved,
        rejected,
        editable,
        loading,
        onUpdate,
    }: ArgumentListItemProps) => {
        const [expanded, setExpanded] = useState(false);
        const [intermediateArgument, setIntermediateArgument] = useState<IArgument>();

        useEffect(() => {
            setExpanded(Boolean(!approved && editable));
        }, [approved, editable]);

        const editorProps: ArgEditorProps = {
            argument,
            groupArgument,
            displayRegulatorArgument,
            searchArgument,
            loading,
            disabled: !editable,
            onChange: async (args, intermediate) => {
                if (intermediate) {
                    setIntermediateArgument(args.find((arg) => arg.id === argument.id));
                } else if (wereArgsChanged([argument], args)) {
                    const updates = args.map((arg) => ({
                        id: arg.id,
                        value: arg.value,
                        md_id: arg.md_id,
                        special_value: (arg.value === null && arg.special_value) || undefined,
                    }));
                    onUpdate?.(updates);
                    setExpanded(false);
                }
                return args;
            },
        };

        const [approveInProgress, setApproveInProgress] = useState(false);
        const onApprove = useCallback(async () => {
            try {
                setApproveInProgress(true);
                if (!intermediateArgument) {
                    handleApprove(argument);
                }
                onUpdate?.(
                    [
                        {
                            id: argument.id,
                            value: argument.value,
                            md_id: argument.md_id,
                            special_value: argument.special_value ?? undefined,
                        },
                    ],
                    !intermediateArgument,
                );
            } finally {
                setApproveInProgress(false);
            }
            setExpanded(false);
        }, [onUpdate, argument, handleApprove, setApproveInProgress]);

        const isDTypeValid = MEDIA_ARGUMENT_DTYPES.includes(argument.dtype);

        const setSpecialValue = useCallback(
            (id: number, specialValue?: Exclude<SpecialValue, 'none'>) => {
                const updates: IArgumentUpdate[] = [];
                if (groupArgument) {
                    updates.push({
                        id: groupArgument.id,
                        md_id: groupArgument.md_id,
                        value: null,
                        special_value: specialValue,
                    });
                }
                updates.push({
                    id,
                    md_id: argument.md_id,
                    value: null,
                    special_value: specialValue,
                });
                onUpdate?.(updates);
                setExpanded(false);
            },
            [withApproval, expanded, onUpdate, groupArgument, argument],
        );

        const onSetUnspecified = (id: number) => setSpecialValue(id, 'unspecified');
        const onSetUnknown = (id: number) => setSpecialValue(id, 'unknown');

        const _onClick = () => {
            if (approved && editable && !expanded) {
                setExpanded(true);
            }
        };

        return (
            <>
                <_ListItem onClick={_onClick} data-argument-name={argument.name}>
                    <ArgCard
                        withApproval={withApproval}
                        active={editable && !approved}
                        approved={approved}
                        rejected={rejected}
                        disabled={!editable}
                    >
                        <Box display="flex" flexDirection="row">
                            {isDTypeValid && (
                                <ErrorBoundary FallbackComponent={ArgErrorFallback}>
                                    <ArgEditor {...editorProps} />
                                </ErrorBoundary>
                            )}
                            {!isDTypeValid && (
                                <Argument
                                    argument={argument}
                                    className={argument.extractor_cls_name.toLowerCase()}
                                    disabled={loading || argument.is_stale}
                                    mutable
                                    onSetUnspecified={onSetUnspecified}
                                    onSetUnknown={onSetUnknown}
                                    onTitleClick={() => setExpanded(!expanded)}
                                >
                                    <_ApprovedIconContainer>
                                        <ArgEditor {...editorProps} />
                                        {withApproval && !expanded && approved && <ApprovedIcon />}
                                    </_ApprovedIconContainer>
                                </Argument>
                            )}
                        </Box>
                        {expanded && withApproval && !rejected && (
                            <_ApproveContainer>
                                {((withApproval && expanded) || argument.show_description) && argument.description && (
                                    <FormHelperText sx={{ mr: 1, flex: 1, fontStyle: 'italic', ml: 2 }}>
                                        {argument.description}
                                    </FormHelperText>
                                )}
                                <Button
                                    disabled={approveInProgress}
                                    color={approved || intermediateArgument ? 'warning' : 'primary'}
                                    variant="contained"
                                    onClick={onApprove}
                                >
                                    {approved || intermediateArgument ? 'Modify' : 'Approve'}
                                </Button>
                            </_ApproveContainer>
                        )}
                    </ArgCard>
                </_ListItem>
            </>
        );
    },
);

QaArgumentListItem.displayName = 'QaArgumentListItem';

export default QaArgumentListItem;
